import invariant from 'tiny-invariant';
import { ConsoleLoggerAdapter } from './ConsoleLoggerAdapter';
import { InternalLogger } from './InternalLogger';
import { NullLogger } from './NullLogger';
import { LibraryLoggingAttributes, LoggerInterface, LoggerOptions, MfeLoggingAttributes } from './loggerModels';

// default to Null Logger, but know we are not initialized yet
let internalLogger: LoggerInterface = new InternalLogger(new NullLogger());
let initialized: boolean = false;
let warned: boolean = false;

// Initialize the internal logger; this can only be done once.
export function initializeLogger(logger: LoggerInterface, loggerOptions?: LoggerOptions): LoggerInterface {
  invariant(!initialized, 'Attempted to reinitialize logger');
  internalLogger = new InternalLogger(logger, loggerOptions);
  invariant(internalLogger, 'Failed to create a logger');
  initialized = true;
  return internalLogger;
}

// get the current internal logger
export function getLogger(attributes?: Object): LoggerInterface {
  // warn one time if we forgot to initialize logging, except when testing
  // jest sets NODE_ENV = 'test'; see https://jestjs.io/docs/environment-variables
  if (!initialized && !warned && process.env.NODE_ENV !== 'test') {
    warned = true;
    console.warn('internalLogger was not initialized');
  }
  if (attributes) return internalLogger.child(attributes);
  return internalLogger;
}

// Create and return standard Library logging attributes structure
export function getLibraryLoggingAttributes(
  name: string,
  version: string,
  additionalMetadata?: object,
): LibraryLoggingAttributes {
  return {
    lib: {
      name,
      version,
    },
    ...additionalMetadata,
  };
}

// Create and return standard MFE logging attributes structure
// environment is the string version of the Environment enum to reduce dependencies
export function getMfeLoggingAttributes(
  name: string,
  version: string,
  gatsbyPhase: string,
  environment: string,
  additionalMetadata?: object,
): MfeLoggingAttributes {
  return {
    mfe: {
      name,
      version,
      gatsbyPhase,
      environment,
    },
    ...additionalMetadata,
  };
}

// creates a console logger and initializes logging with it
// used for gatsby node and ssr files
export function initConsoleLogger(attributes: Object, loggerOptions?: LoggerOptions) {
  const loggingAdapter = new ConsoleLoggerAdapter(attributes);
  const logger = initializeLogger(loggingAdapter, loggerOptions);
  logger.debug('Console logger initialized');
}

// creates a null logger and initializes logging with it
// effectively disabled logging
export function initNullLogger() {
  const loggingAdapter = new NullLogger();
  const logger = initializeLogger(loggingAdapter);
  logger.debug('Null logger initialized');
}

// initializes logging with the specified adapter
// used for gatsby browser files
// eliminates coupling to specific logger  e.g. SumoLoggerAdapter
export function initCustomLogger(loggingAdapter: LoggerInterface, loggerOptions?: LoggerOptions) {
  const logger = initializeLogger(loggingAdapter, loggerOptions);
  logger.debug('Custom logger initialized');
}
